import { Injectable, isDevMode } from "@angular/core";

import moment from "moment";

type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";

interface LogLevels {
  [key: string]: number;
}

interface LogColors {
  [key: string]: string;
}

type LogMessage = string | number | boolean | Error | object | null | any | undefined;

@Injectable({
  providedIn: "root"
})
export class LoggerService {
  private _levels: LogLevels = {
    trace: 5,
    debug: 4,
    info: 3,
    warn: 2,
    error: 1,
    fatal: 0
  };

  private _colors: LogColors = {
    trace: "#A0A0A0", // light gray
    debug: "#00FF00", // green
    info: "#ffa500", // orange
    warn: "#FFFF00", // yellow
    error: "#FF0000", // red
    fatal: "#FF0000" // red
  };

  private _isProduction = !isDevMode();

  private parser(param: LogMessage): string {
    if (param === null || param === undefined) {
      return "";
    }
    if (typeof param === "string" || typeof param === "number" || typeof param === "boolean") {
      return param.toString();
    }
    if (param instanceof Error) {
      return param.message;
    }
    return Object.keys(param).length ? JSON.stringify(param, null, 2) : "";
  }

  private formatter(level: LogLevel, message: LogMessage, meta?: LogMessage): string {
    const timestamp = moment().format("HH:mm:ss");
    const metaMsg = meta !== undefined ? `: ${this.parser(meta)}` : "";
    return `%c${timestamp} [${level.toUpperCase()}] ${this.parser(message)}${metaMsg}`;
  }

  private log(level: LogLevel, message: LogMessage, meta?: LogMessage): void {
    if (this._isProduction && this._levels[level] > this._levels.error) {
      return;
    }

    const formattedMessage = this.formatter(level, message, meta);
    const color = this._colors[level];

    switch (level) {
      case "trace":
      case "debug":
        console.log(formattedMessage, `color: ${color}`);
        break;
      case "info":
        console.info(formattedMessage, `color: ${color}`);
        break;
      case "warn":
        console.warn(formattedMessage, `color: ${color}`);
        break;
      case "error":
      case "fatal":
        // For errors, we need to check if it's an actual Error object to preserve stack trace
        if (meta instanceof Error) {
          console.error(formattedMessage, `color: ${color}`, "\n", meta);
        } else {
          console.error(formattedMessage, `color: ${color}`);
        }
        break;
    }
  }

  trace(message: LogMessage, meta?: LogMessage): void {
    this.log("trace", message, meta);
  }

  debug(message: LogMessage, meta?: LogMessage): void {
    this.log("debug", message, meta);
  }

  info(message: LogMessage, meta?: LogMessage): void {
    this.log("info", message, meta);
  }

  warn(message: LogMessage, meta?: LogMessage): void {
    this.log("warn", message, meta);
  }

  error(message: LogMessage, meta?: LogMessage): void {
    this.log("error", message, meta);
  }

  fatal(message: LogMessage, meta?: LogMessage): void {
    this.log("fatal", message, meta);
  }
}
